import type { ReactNode } from 'react'
import { useKitchensink } from '../context'
import { theme } from '../theme'

const codeStyle = {
  fontFamily: 'monospace',
  fontSize: '12px',
  color: theme.accent,
  backgroundColor: theme.accentSubtle,
  borderRadius: '4px',
  padding: '1px 5px',
} as const

type StepProps = { n: number; children: ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <span
      style={{
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '1px',
      }}
    >
      {n}
    </span>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

export const EngagementPage = () => {
  const { variant } = useKitchensink()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
      <div>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: theme.text,
            margin: '0 0 6px',
            letterSpacing: '-0.02em',
          }}
        >
          ⏱️ Engagement Tracking Page
        </h1>
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
          Route: <code style={codeStyle}>{variant === 'spa' ? '/engagement' : '/engagement/'}</code>
        </p>
      </div>

      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <p
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            margin: '0 0 4px',
          }}
        >
          How to test
        </p>
        <Step n={1}>
          Fill in the tenant credentials above and click{' '}
          <strong style={{ color: theme.text }}>Initialize</strong> to start the snippet.
        </Step>
        <Step n={2}>
          Open the browser console. After ~10 seconds you should see a{' '}
          <code style={codeStyle}>[jitsu] track Engagement</code> log with{' '}
          <code style={codeStyle}>engagementMsec ≈ 10000</code>. This is the heartbeat firing.
        </Step>
        <Step n={3}>
          Switch to another tab. A <code style={codeStyle}>[jitsu] track Engagement</code> log
          should fire immediately with the partial time since the last heartbeat. Switch back — the
          timer resumes and the next heartbeat only counts visible time.
        </Step>
        <Step n={4}>
          Navigate to another page (e.g. <strong style={{ color: theme.text }}>Home</strong>).{' '}
          {variant === 'spa' ? (
            <>
              A flush <code style={codeStyle}>[jitsu] track Engagement</code> fires immediately,
              followed by a <code style={codeStyle}>[jitsu] page</code> log. A new{' '}
              <code style={codeStyle}>pageViewId</code> is set in context from that point on.
            </>
          ) : (
            <>
              A flush <code style={codeStyle}>[jitsu] track Engagement</code> fires before the page
              unloads, and the fresh page load emits a new{' '}
              <code style={codeStyle}>[jitsu] page</code> with a new{' '}
              <code style={codeStyle}>pageViewId</code>.
            </>
          )}
        </Step>
      </div>

      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <p
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            margin: '0 0 4px',
          }}
        >
          Event shape
        </p>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: '12px',
            color: theme.text,
            backgroundColor: theme.surfaceElevated,
            borderRadius: '8px',
            padding: '14px 16px',
            overflowX: 'auto',
            lineHeight: 1.6,
          }}
        >{`jitsu.track('Engagement', {
  engagementMsec: 10012,  // delta since last emission, in ms
})

// pageViewId is set in Jitsu context on every jitsu.page() call
// and automatically included in all subsequent events`}</pre>
      </div>
    </div>
  )
}
